import { useEffect, useState } from 'react';
import { categoryService } from '../services/api';

/**
 * Cache de categorías para ahorrar requests al backend en cada navegación.
 *
 * - Cache singleton en memoria: 1 fetch por load del frontend, compartido por
 *   todos los callers que usan este hook (mismo módulo, mismo singleton).
 * - localStorage TTL 24h: sobrevive refreshes y tabs nuevas.
 * - Las categorías son efectivamente estáticas en producción — si las modificás
 *   server-side y querés forzar refresh, llamá invalidateCategoriesCache() o
 *   esperá el TTL.
 *
 * El hook devuelve la lista ya ordenada por sortOrder asc (fallback a name).
 *
 * Manejo de errores: si el fetch falla, log a console.error (no se silencia
 * con catch vacío) y se mantiene el cache anterior si lo hay.
 */

// v2: cambió el sort canónico — invalida automáticamente cualquier v1 cacheado.
const CACHE_KEY = 'vatuco.categories.v2';
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

let memoryCache = null;
let inFlight = null;

// Orden de display canónico por nombre. El backend hoy ordena alfabético — esto
// pisa ese orden en el cliente. Si el set de categorías cambia en backend, hay
// que actualizar este map (o agregar las nuevas: las no listadas caen al final).
const SORT_BY_NAME = {
  'Ropa':                  1,
  'Belleza y Cuidado':     2,
  'Tecnología':            3,
  'Electrodomésticos':     4,
  'Libros y Apuntes':      5,
  'Hogar y Deco':          6,
  'Cocina y Mate':         7,
  'Deportes y Aire Libre': 8,
  'Juegos y Hobbies':      9,
  'Niños y Bebés':        10,
  'Servicios':            11,
  'Otros':                12,
};

function sortCategories(list) {
  return [...list].sort((a, b) => {
    const ao = SORT_BY_NAME[a.name] ?? a.sortOrder ?? 999;
    const bo = SORT_BY_NAME[b.name] ?? b.sortOrder ?? 999;
    if (ao !== bo) return ao - bo;
    return (a.name || '').localeCompare(b.name || '');
  });
}

function readFromLocalStorage() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.cachedAt || !Array.isArray(parsed.data)) return null;
    if (Date.now() - parsed.cachedAt > TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeToLocalStorage(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ cachedAt: Date.now(), data }));
  } catch {
    // localStorage puede fallar (incógnito, quota, etc.) — no es crítico
  }
}

async function fetchAndCache() {
  const r = await categoryService.getCategories();
  // El interceptor de api.js ya desempaqueta DataResponse, así que r.data debería
  // ser el array directo. Aceptamos también shape sin desempaquetar por defensa.
  const raw = r?.data;
  const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
  if (!list.length) {
    // eslint-disable-next-line no-console
    console.warn('[useCategories] backend devolvió 0 categorías. Shape recibido:', raw);
  }
  const sorted = sortCategories(list);
  memoryCache = sorted;
  writeToLocalStorage(sorted);
  return sorted;
}

export function invalidateCategoriesCache() {
  memoryCache = null;
  inFlight = null;
  try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

export function useCategories() {
  const [categories, setCategories] = useState(() => {
    if (memoryCache) return memoryCache;
    const fromStorage = readFromLocalStorage();
    if (fromStorage) {
      memoryCache = fromStorage;
      return fromStorage;
    }
    return [];
  });
  const [loading, setLoading] = useState(() => !memoryCache && !readFromLocalStorage());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (memoryCache) return;
    let isMounted = true;

    if (!inFlight) {
      inFlight = fetchAndCache().finally(() => { inFlight = null; });
    }
    inFlight
      .then(list => { if (isMounted) { setCategories(list); setLoading(false); setError(null); } })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('[useCategories] error al cargar categorías:', err);
        if (isMounted) { setLoading(false); setError(err); }
      });

    return () => { isMounted = false; };
  }, []);

  return { categories, loading, error };
}
